/** Earnings & Estimates tab. Mock data only.
 * A predicted date is labelled predicted. It is never presented as confirmed.
 */
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  CachedBadge, DataNotice, DeltaText, EarningsQualityBadge, KeyRow, MockBadge, PanelGrid, StatTile, TileRow,
  WorkspacePanel,
} from "@/workspace/primitives";
import { earnings } from "@/workspace/mockTickerData";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

const QUALITY_MEANING = {
  confirmed: "The company has announced this date.",
  estimated: "Existing cached status is estimated, not confirmed.",
  expected: "Derived from the historical reporting cadence and corroborated by consensus. Not a company announcement.",
  predicted: "A model estimate from the historical reporting cadence only. It can move.",
  unavailable: "No date is available for this ticker.",
};

export function EarningsTab({ t }) {
  const e = t.live ? null : earnings(t.ticker);
  const usable = t.earnings.quality === "confirmed" || t.earnings.quality === "expected" || t.earnings.quality === "estimated";
  const live = t.fundamentalsLive || {};

  if (t.live) {
    return (
      <div className="space-y-3">
        <TileRow cols="grid-cols-2 sm:grid-cols-4">
          <StatTile
            label={t.earnings?.past ? "Most recent earnings date" : "Next earnings date"}
            value={t.earnings.date || "Date unavailable"}
            badge={t.earnings?.past
              ? null
              : <EarningsQualityBadge quality={t.earnings.quality} testid="earnings-quality-badge" />}
            testid="earnings-tile-date"
          />
          <StatTile
            label={t.earnings?.past ? "Next event" : "Days to earnings"}
            value={t.earnings?.past ? "—" : (t.earnings.daysTo != null ? `${t.earnings.daysTo}d` : "—")}
            sub={t.earnings?.past ? "Past date supplied — next event unavailable" : undefined}
          />
          <StatTile
            label="Date quality"
            value={t.earnings?.past ? "Past date supplied" : t.earnings.quality}
            sub={t.earnings?.past ? "Next event unavailable" : QUALITY_MEANING[t.earnings.quality]}
            testid="earnings-tile-quality"
          />
          <StatTile label="Average post-print move" value="—" sub={LIVE_UNAVAILABLE.printMove} />
        </TileRow>
        <PanelGrid>
          <WorkspacePanel title="Estimates" notice={<CachedBadge />} testid="earnings-estimates">
            <KeyRow label="Analyst mean target">{live.targetMean ?? LIVE_UNAVAILABLE.notInCache}</KeyRow>
            <KeyRow label="Analyst count">{live.analystCount ?? LIVE_UNAVAILABLE.notInCache}</KeyRow>
            <p className="text-[11px] mt-2" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.estimateHistory}</p>
            <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>{LIVE_UNAVAILABLE.analystDispersion}</p>
          </WorkspacePanel>
          <WorkspacePanel title="Historical results and surprise" notice={<CachedBadge />} testid="earnings-history">
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.surpriseHistory}</p>
          </WorkspacePanel>
        </PanelGrid>
        <WorkspacePanel title="Earnings-risk treatment for income strategies" notice={<CachedBadge />} testid="earnings-treatment">
          <StatusBadge
            tier={t.earnings?.past
              ? "info"
              : (t.earnings.quality === "confirmed" && t.earnings.daysTo != null && t.earnings.daysTo <= 21 ? "caution" : "info")}
            text={t.earnings?.past
              ? "Past date supplied — next event unavailable"
              : (t.earnings.quality === "unavailable" ? "Earnings date unavailable" : `${t.earnings.quality} date`)}
          />
          <div className="mt-2 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
            <KeyRow label={t.earnings?.past ? "Most recent earnings date" : "Date"}>{t.earnings.date || "Date unavailable"}</KeyRow>
            <KeyRow label="Quality">{t.earnings?.past ? "Past date supplied" : t.earnings.quality}</KeyRow>
            <KeyRow label="Days out">{t.earnings?.past ? "Next event unavailable" : (t.earnings.daysTo != null ? `${t.earnings.daysTo}d` : "—")}</KeyRow>
          </div>
          <p className="text-[10px] mt-2" style={{ color: "var(--hoip-text-tertiary)" }}>{LIVE_UNAVAILABLE.predictedEarnings}</p>
        </WorkspacePanel>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <TileRow cols="grid-cols-2 sm:grid-cols-4">
        <StatTile
          label="Next earnings date"
          value={t.earnings.date}
          badge={<EarningsQualityBadge quality={t.earnings.quality} testid="earnings-quality-badge" />}
          testid="earnings-tile-date"
        />
        <StatTile label="Days to earnings" value={`${t.earnings.daysTo}d`} sub="Countdown from the last session" />
        <StatTile
          label="Date quality"
          value={t.earnings.quality}
          sub={QUALITY_MEANING[t.earnings.quality]}
          testid="earnings-tile-quality"
        />
        <StatTile
          label="Average post-print move"
          value={`${(e.history.reduce((a, b) => a + Math.abs(b.movePct), 0) / e.history.length).toFixed(1)}%`}
          sub="Absolute, last four prints"
        />
      </TileRow>

      <PanelGrid>
        <WorkspacePanel
          title="Estimates"
          notice={<MockBadge />}
          hint="Consensus figures are fixtures. No estimate provider is wired."
          testid="earnings-estimates"
        >
          <div className="flex items-center gap-2 text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span className="w-[24%] shrink-0">Period</span>
            <span className="w-[30%] shrink-0 text-right">Revenue est.</span>
            <span className="w-[24%] shrink-0 text-right">EPS est.</span>
            <span className="flex-1 text-right">Analysts</span>
          </div>
          {e.estimates.map((r) => (
            <div key={r.period} className="flex items-center gap-2 text-[11px] py-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="w-[24%] shrink-0">{r.period}</span>
              <span className="w-[30%] shrink-0 text-right font-mono-data">{r.revenueEst}</span>
              <span className="w-[24%] shrink-0 text-right font-mono-data">{r.epsEst}</span>
              <span className="flex-1 text-right font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{r.analysts}</span>
            </div>
          ))}
        </WorkspacePanel>

        <WorkspacePanel
          title="Historical results and surprise"
          notice={<MockBadge />}
          testid="earnings-history"
        >
          <div className="flex items-center gap-2 text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span className="w-[20%] shrink-0">Period</span>
            <span className="w-[18%] shrink-0 text-right">Est.</span>
            <span className="w-[18%] shrink-0 text-right">Actual</span>
            <span className="w-[22%] shrink-0 text-right">Surprise</span>
            <span className="flex-1 text-right">Next-day</span>
          </div>
          {e.history.map((r) => (
            <div key={r.period} className="flex items-center gap-2 text-[11px] py-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="w-[20%] shrink-0">{r.period}</span>
              <span className="w-[18%] shrink-0 text-right font-mono-data">{r.epsEst.toFixed(2)}</span>
              <span className="w-[18%] shrink-0 text-right font-mono-data">{r.epsActual.toFixed(2)}</span>
              <span className="w-[22%] shrink-0 text-right"><DeltaText value={r.surprisePct} suffix="%" digits={1} /></span>
              <span className="flex-1 text-right"><DeltaText value={r.movePct} suffix="%" digits={1} /></span>
            </div>
          ))}
        </WorkspacePanel>
      </PanelGrid>

      <WorkspacePanel
        title="Earnings-risk treatment for income strategies"
        notice={<MockBadge />}
        hint="Illustrative. Production applies the earnings modifier inside the existing confidence and ranking logic."
        testid="earnings-treatment"
      >
        <div className="mb-2">
          {usable
            ? <StatusBadge tier={t.earnings.daysTo <= 21 ? "caution" : "info"} text={t.earnings.daysTo <= 21 ? "Print inside a typical tenor" : "Print clear of a typical tenor"} />
            : <StatusBadge tier="caution" text="Date quality too weak to size earnings risk" />}
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
          {e.treatment}
        </p>
        <div className="mt-2 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
          <KeyRow label="Date">{t.earnings.date}</KeyRow>
          <KeyRow label="Quality">{t.earnings.quality}</KeyRow>
          <KeyRow label="Days out">{t.earnings.daysTo}d</KeyRow>
        </div>
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <DataNotice adminText="Existing earnings fields are next_earnings_date, next_earnings_status (estimated | confirmed), days_to_earnings and earnings_passed. The four-way quality ladder on this tab is a mock extension of that field." />
        </div>
      </WorkspacePanel>
    </div>
  );
}
